// How a customer is presented on a printed document.
//
// Staff onboard a customer as either an individual or a company, and the two
// store their details differently: for a company the customer's `name` IS the
// company name and `contact_person` is the person to deal with, while for an
// individual `name` is that person. Portal accounts are the other way round —
// `name` is a person and `company_name`, when present, is the organisation they
// buy for. Printing one fixed set of labels for all of them duplicated the
// company name, printed "Company: N/A" on individuals, and left the TIN — the
// one thing a company invoice must carry — off the document entirely.

const esc = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// individual/company describe the buyer; retail/wholesale/vip are portal price
// tiers, which say nothing about who the buyer is and do not belong on their
// document.
const ENTITY_LABELS = { company: 'Company', individual: 'Individual' };

export const customerDetails = (snapshot = {}, live = {}) => {
  const pick = (field) => snapshot[field] || live[field] || '';
  const name = pick('name');
  const companyName = pick('company_name');
  const type = pick('customer_type');
  // A staff-registered company keeps its name in `name`; a portal account
  // carrying a separate organisation name is a person buying for that company.
  const isCompany = type === 'company' || (!!companyName && companyName !== name);
  return {
    isCompany,
    // A company on the staff register bills in its own name; anyone else bills
    // in their own name and may have an organisation beside it.
    registeredAsCompany: type === 'company',
    name,
    companyName,
    contactPerson: pick('contact_person'),
    tin: pick('tin_number'),
    phone: pick('phone'),
    email: pick('email'),
    address: pick('address'),
    deliveryPoint: pick('delivery_point'),
    entityLabel: ENTITY_LABELS[type] || (isCompany ? 'Company' : ''),
  };
};

// Label/value pairs for the "bill to" block, with everything blank left out so
// a document never prints a row of N/A.
export const customerRows = (details, { includeAddress = true } = {}) => {
  const company = details.registeredAsCompany;
  const rows = [
    ['Bill To', (company ? details.name || details.companyName : details.name) || 'N/A'],
    // The company's contact person, or the organisation a person buys for —
    // never the customer's own name repeated back.
    [company ? 'Contact Person' : 'Company',
     company ? details.contactPerson : (details.companyName !== details.name ? details.companyName : '')],
    ['TIN', details.tin],
    ['Phone', details.phone],
    ['Email', details.email],
    [includeAddress ? 'Address' : null, includeAddress ? details.address : null],
    ['Delivery Point', details.deliveryPoint],
    ['Customer Type', details.entityLabel],
  ];
  return rows.filter(([label, value]) => label && value);
};

// The same rows rendered for the print windows, which build raw HTML. They are
// laid out as a two-column band rather than a line per field, so the party
// details do not run down a third of the page before the goods appear.
export const customerRowsHtml = (snapshot, live, options = {}) =>
  `<div class="party">${customerRows(customerDetails(snapshot, live), options)
    .map(([label, value], index) =>
      `<div class="pf${index === 0 ? ' pf-main' : ''}"><span class="pf-l">${esc(label)}</span><span class="pf-v">${esc(value)}</span></div>`)
    .join('')}</div>`;

// Shared by every printed document, so the block looks the same on all of them.
export const PARTY_CSS = `
  .party { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 32px; margin-bottom: 8px; }
  .pf { display: flex; gap: 8px; font-size: 13px; line-height: 1.5; }
  .pf-l { color: #6b7280; min-width: 84px; }
  .pf-v { font-weight: 600; }
  .pf-main { grid-column: 1 / -1; font-size: 15px; margin-bottom: 4px; }
  .pf-main .pf-v { font-weight: 700; }
`;

// One-line form for pickers and tables: the company name on its own, or a
// person with the organisation they buy for.
export const customerLine = (customer = {}) => {
  const { name, companyName, isCompany } = customerDetails(customer);
  if (isCompany) return companyName && companyName !== name ? `${name} - ${companyName}` : name;
  return name;
};
